import React from 'react';
import { useIntl } from 'react-intl';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { addSongFlowMessages } from './addSongFlowMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepActionsProps {
  onBack?: () => void;
  /** A line before the buttons ("We save the song when you press Next."). */
  note?: string;
  /** "Skip for now". Absent on step 1: the title is required. */
  onSkip?: () => void;
  /** The main button. Absent when the step has its own buttons. */
  main?: { label: string; onClick: () => void; disabled?: boolean; isLoading?: boolean; testID?: string };
  disabled?: boolean;
  /** Says why the main button is off. It shows on every screen size, also on a phone (QA imp-3). */
  offHint?: string;
}

/**
 * The action row of a step (design catalog-add-song a1-i4): Back, a note, "Skip for now" and the
 * main button. Each label names one action; there is no mixed label like "Save and design".
 */
const StepActionsComponent: React.FC<StepActionsProps> = ({
  onBack,
  note,
  onSkip,
  main,
  disabled = false,
  offHint,
}) => {
  const intl = useIntl();
  return (
    <>
      {offHint !== undefined && (
        <p className={styles.offHint} data-testid="add-song-off-hint">
          {offHint}
        </p>
      )}
      <div className={styles.actions}>
        {onBack !== undefined && (
          <SMLButton
            color="link"
            omitCap
            leftIcon={faChevronLeft}
            onClick={onBack}
            disabled={disabled}
            testID="add-song-back"
            ariaLabel={intl.formatMessage(addSongFlowMessages.back)}
          >
            {/* A phone shows only the arrow, so the row stays one line (QA mob-17). */}
            <span className={styles.backLabel}>{intl.formatMessage(addSongFlowMessages.back)}</span>
          </SMLButton>
        )}
        <span className={styles.actionsNote}>{note}</span>
        {onSkip !== undefined && (
          <SMLButton color="link" omitCap onClick={onSkip} disabled={disabled} testID="add-song-skip">
            {intl.formatMessage(addSongFlowMessages.skip)}
          </SMLButton>
        )}
        {main !== undefined && (
          <SMLButton
            color="primary"
            size="lg"
            omitCap
            rightIcon={faChevronRight}
            onClick={main.onClick}
            disabled={disabled || main.disabled === true}
            isLoading={main.isLoading}
            testID={main.testID ?? 'add-song-next'}
          >
            {main.label}
          </SMLButton>
        )}
      </div>
    </>
  );
};

export const StepActions = TypedMemo(StepActionsComponent);
